import React, { useState, useEffect } from 'react';
import {
  Box, Button, Alert, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControlLabel, Switch, Chip
} from '@mui/material';
import { Add, Edit, Delete, Star } from '@mui/icons-material';
import companyService from '../../services/company.service';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../i18n/LanguageContext';

const CompaniesTab: React.FC = () => {
  const { t } = useLanguage();
  const { currentCompanyId, switchCompany } = useAuth();
  const [companies, setCompanies] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({
    code: '', name: '', taxId: '', registrationNumber: '',
    address: '', city: '', country: '', phone: '', email: '', website: '',
    bankName: '', bankAccount: '', bankSwift: '', isActive: true
  });

  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    try {
      const res = await companyService.getAll();
      setCompanies(res.data);
    } catch (err) { setError('Failed to load companies'); }
  };

  const openNew = () => {
    setEditId(null);
    setForm({
      code: '', name: '', taxId: '', registrationNumber: '',
      address: '', city: '', country: '', phone: '', email: '', website: '',
      bankName: '', bankAccount: '', bankSwift: '', isActive: true
    });
    setDialog(true);
  };

  const openEdit = (c: any) => {
    setEditId(c.id);
    setForm({
      code: c.code || '', name: c.name || '', taxId: c.taxId || '',
      registrationNumber: c.registrationNumber || '',
      address: c.address || '', city: c.city || '', country: c.country || '',
      phone: c.phone || '', email: c.email || '', website: c.website || '',
      bankName: c.bankName || '', bankAccount: c.bankAccount || '',
      bankSwift: c.bankSwift || '', isActive: c.isActive
    });
    setDialog(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      if (editId) await companyService.update(editId, form);
      else await companyService.create(form);
      setDialog(false);
      setSuccess(editId ? t('common.saved') : t('common.created'));
      await loadCompanies();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving company');
    }
  };

  const handleDelete = async (id: number) => {
    if (id === currentCompanyId) {
      setError(t('company.cannot_delete_current'));
      return;
    }
    if (!window.confirm(t('common.confirm_delete'))) return;
    try {
      await companyService.delete(id);
      await loadCompanies();
    } catch (err: any) { setError(err.response?.data?.message || 'Error deleting company'); }
  };

  const handleSetDefault = async (companyId: number) => {
    try {
      await companyService.setDefault(companyId);
      setSuccess(t('company.default_set'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) { setError(err.response?.data?.message || 'Error'); }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={openNew}>{t('company.new')}</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('company.code')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('company.name')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('company.city')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('company.country')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('common.status')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.actions')}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {companies.map(c => (
              <TableRow key={c.id} sx={{ bgcolor: c.id === currentCompanyId ? '#e3f2fd' : undefined }}>
                <TableCell>
                  {c.code}
                  {c.id === currentCompanyId && <Chip label={t('company.current')} size="small" color="primary" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.city}</TableCell>
                <TableCell>{c.country}</TableCell>
                <TableCell>
                  <Chip label={c.isActive ? t('common.active') : t('common.inactive')}
                    color={c.isActive ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" title={t('company.set_default')} onClick={() => handleSetDefault(c.id)}>
                    <Star fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => openEdit(c)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(c.id)}
                    disabled={c.id === currentCompanyId}>
                    <Delete fontSize="small" />
                  </IconButton>
                  {c.id !== currentCompanyId && (
                    <Button size="small" variant="outlined" sx={{ ml: 1 }}
                      onClick={() => switchCompany(c.id)}>{t('company.switch')}</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? t('company.edit') : t('company.new')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField fullWidth required label={t('company.code')} value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
              inputProps={{ maxLength: 20 }} disabled={!!editId} />
            <TextField fullWidth required label={t('company.name')} value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth label={t('company.tax_id')} value={form.taxId}
              onChange={e => setForm({ ...form, taxId: e.target.value })} />
            <TextField fullWidth label={t('company.registration_number')} value={form.registrationNumber}
              onChange={e => setForm({ ...form, registrationNumber: e.target.value })} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth label={t('company.address')} value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })} />
            <TextField fullWidth label={t('company.city')} value={form.city}
              onChange={e => setForm({ ...form, city: e.target.value })} />
            <TextField fullWidth label={t('company.country')} value={form.country}
              onChange={e => setForm({ ...form, country: e.target.value })} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth label={t('company.phone')} value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} />
            <TextField fullWidth label={t('company.email')} value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })} />
            <TextField fullWidth label={t('company.website')} value={form.website}
              onChange={e => setForm({ ...form, website: e.target.value })} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth label={t('company.bank_name')} value={form.bankName}
              onChange={e => setForm({ ...form, bankName: e.target.value })} />
            <TextField fullWidth label={t('company.bank_account')} value={form.bankAccount}
              onChange={e => setForm({ ...form, bankAccount: e.target.value })} />
            <TextField fullWidth label={t('company.bank_swift')} value={form.bankSwift}
              onChange={e => setForm({ ...form, bankSwift: e.target.value })} />
          </Box>
          <FormControlLabel sx={{ mt: 1 }} control={
            <Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
          } label={t('common.active')} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.code || !form.name}>
            {editId ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompaniesTab;
